import React, { memo, useCallback } from 'react';
import { EditorStarterItem } from '../../items/item-type';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { useSelectionValues } from '../utils/use-selection-values';
import { InspectorSubLabel } from '../components/inspector-label';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { Opacity } from '@crabs-ai/web-icons';

const ClipOpacityControlsUnmemoized: React.FC<{
  opacity: number;
  itemId: string;
}> = ({ opacity, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<EditorStarterItem, number>(
    itemId,
    (item) => item.opacity,
  );

  const setOpacity = useCallback(
    (newOpacity: number, commitToUndoStack: boolean) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            const prev = i as EditorStarterItem;
            if (prev.opacity === newOpacity) {
              return prev;
            }
            return {
              ...prev,
              opacity: newOpacity,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  const handleSliderChange = useCallback(
    (value: number, commitToUndoStack: boolean) => {
      const newOpacity = value / 100;
      setOpacity(newOpacity, commitToUndoStack);
    },
    [setOpacity],
  );

  const opacityPercent = Math.round(opacity * 100);

  return (
    <div className="flex w-full flex-col items-start justify-center">
      <InspectorSubLabel>
        {t('Editor.inspector.controls.clip-opacity.title')}
      </InspectorSubLabel>
      <ValueInputField
        setValue={(update) => {
          handleSliderChange(update.num, update.commitToUndoStack);
        }}
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <Opacity className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        isUnit="%"
        value={opacityPercent}
        min={0}
        max={100}
        step={1}
        accessibilityLabel="Opacity"
        className={'w-full'}
        hasMixedValue={isMixed}
      />
    </div>
  );
};

export const ClipOpacityControls = memo(ClipOpacityControlsUnmemoized);
